import { useEffect } from 'react';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import type { PublishJobDto, PublishJobSummary } from '@course-authoring/shared-types';
import { fetchQuestionPublishJob } from '../../api/questionApi';
import { PUBLISH_JOB_POLL_INTERVAL_MS } from '../../utils/publishJobPolling';

export type LessonQuestionPreparationData = PublishJobDto | PublishJobSummary | null;

export const questionPreparationKeys = {
  all: ['question-preparation'] as const,
  detail: (questionId: number) => ['question-preparation', questionId] as const,
};

export function isActiveLessonQuestionPreparation(
  job: Pick<PublishJobSummary, 'status'> | null | undefined,
): boolean {
  return job?.status === 'queued' || job?.status === 'running';
}

export function seedLessonQuestionPreparation(
  queryClient: QueryClient,
  questionId: number,
  summary: PublishJobSummary | null,
): void {
  queryClient.setQueryData<LessonQuestionPreparationData>(questionPreparationKeys.detail(questionId), summary);
}

/** Question blocks share one bounded status read per question and poll only active work. */
export function useLessonQuestionPreparation(questionId: number | undefined) {
  const queryKey = questionId === undefined
    ? [...questionPreparationKeys.all, 'unselected'] as const
    : questionPreparationKeys.detail(questionId);

  const query = useQuery<LessonQuestionPreparationData>({
    queryKey,
    queryFn: () => questionId === undefined ? Promise.resolve(null) : fetchQuestionPublishJob(questionId),
    enabled: questionId !== undefined,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: (query) => isActiveLessonQuestionPreparation(query.state.data)
      ? PUBLISH_JOB_POLL_INTERVAL_MS
      : false,
    refetchIntervalInBackground: false,
    // The app-wide placeholderData setting carries results across keys; a new
    // question selection must show Checking until its own read or seed arrives.
    placeholderData: () => undefined,
  });

  useEffect(() => {
    if (questionId === undefined) return undefined;
    const refreshVisibleSelection = () => {
      if (document.visibilityState !== 'hidden') void query.refetch({ cancelRefetch: false });
    };
    window.addEventListener('focus', refreshVisibleSelection);
    return () => window.removeEventListener('focus', refreshVisibleSelection);
  }, [questionId, query.refetch]);

  return query;
}

export function isFullLessonQuestionPreparation(
  job: LessonQuestionPreparationData | undefined,
): job is PublishJobDto {
  return job !== null && job !== undefined
    && Array.isArray((job as PublishJobDto).stepResults)
    && typeof (job as PublishJobDto).result === 'object';
}
